import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * TealiumRouteTracker
 *
 * Fires a Tealium pageview (`window.utag.view`) on every react-router route
 * change in the SPA. The Tealium loader (installed in public/index.html)
 * already fires the *first* view on initial DOM load, so this hook skips
 * the *first URL it ever sees* and only emits on subsequent navigations.
 *
 * Why module-level state instead of useRef:
 *   React 18 StrictMode (and effectively any parent re-render that re-keys
 *   the tree) can remount this component on a route change, which resets
 *   `useRef(true)` to `true` for what should be a "subsequent" navigation
 *   and causes the first real client-side route change to be silently
 *   skipped. By tracking the last fired URL at module scope, the dedupe
 *   survives remounts. Server-side rendering is not a concern here (CRA
 *   client-only), so a module-level mutable singleton is safe.
 *
 * Defensive design:
 *   • If utag.js hasn't finished loading yet (it's async), we silently
 *     no-op for that navigation. The initial-URL skip already covers the
 *     case where the loader hasn't fired its first view, and subsequent
 *     route changes are virtually guaranteed to land after utag is ready.
 *   • Identical consecutive URLs are deduped (StrictMode double-mounts +
 *     duplicate location notifications).
 *   • Errors inside utag are swallowed — analytics must never break the app.
 *
 * Mount once, inside <BrowserRouter>, near the top of the React tree.
 */

// HMR-robust singleton: track the most recent URL we've already accounted
// for on `window` rather than a module-level `let`. CRA's dev-mode HMR can
// re-evaluate the module mid-session and would otherwise reset the state to
// null, silently skipping the next route's pageview. Putting it on `window`
// keeps it stable across module re-evaluations and across React StrictMode
// remounts. SSR is not a concern here (CRA client-only).
const URL_STATE_KEY = '__apexTealiumLastUrl';

const getLastObservedUrl = () => {
  if (typeof window === 'undefined') return null;
  return window[URL_STATE_KEY] === undefined ? null : window[URL_STATE_KEY];
};
const setLastObservedUrl = (url) => {
  if (typeof window === 'undefined') return;
  window[URL_STATE_KEY] = url;
};

const TealiumRouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const currentUrl = location.pathname + location.search;
    const last = getLastObservedUrl();

    // First URL this session has seen — utag's own loader already fired
    // the initial pageview for it, so we just record and return.
    if (last === null) {
      setLastObservedUrl(currentUrl);
      return;
    }

    // Same URL as our last observation → no-op (StrictMode double-effect,
    // duplicate location notifications, replace-state with same args, etc.)
    if (currentUrl === last) return;
    setLastObservedUrl(currentUrl);

    if (typeof window === 'undefined') return;
    const utag = window.utag;
    if (!utag || typeof utag.view !== 'function') {
      // utag.js still loading or blocked (ad blocker, etc.) — no-op.
      return;
    }

    try {
      utag.view({
        tealium_event: 'view',
        page_path: location.pathname,
        page_search: location.search || '',
        page_url: window.location.href,
        page_title: document.title || '',
        // Carry the ExternalUid through so any tag depending on it sees
        // it on every navigation, not just the initial load.
        ExternalUid: (window.Parameters && window.Parameters.ExternalUid) || null,
      });
    } catch {
      // Analytics failures must never break navigation.
    }
  }, [location.pathname, location.search]);

  return null;
};

export default TealiumRouteTracker;
